

/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import PropTypes from 'prop-types';

// @mui material components
import Grid from "@mui/material/Grid";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SubgroupCard from "nextus/Groups/GroupCard/Subgroup";
import PlaceholderCard from "examples/Cards/PlaceholderCard";

// Project page components
import Header from "layouts/pages/profile/components/Header";

import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { loadToken, getAllGroups } from 'api/groups';
import { useAuth0 } from "@auth0/auth0-react";

// API/MODELS
import groupAPI from "api/groups";
import bksAPI from "api/bks";
import userAPI from "api/user";

// Images
import team1 from "assets/images/team-1.jpg";
import team2 from "assets/images/team-2.jpg";
import team3 from "assets/images/team-3.jpg";
import team4 from "assets/images/team-4.jpg";
import team5 from "assets/images/team-5.jpg";
import logoSlack from "assets/images/small-logos/logo-slack.svg";
import logoSpotify from "assets/images/small-logos/logo-spotify.svg";
import logoXD from "assets/images/small-logos/logo-xd.svg";
import logoAsana from "assets/images/small-logos/logo-asana.svg";
import logoInvision from "assets/images/small-logos/logo-invision.svg";
import DashboardCardLayout from "nextus/Dashboards/DashboardCardLayout";
import { processDevicesData } from "helper/device";
import { processGroupData } from "helper/group";
import { useMatch } from "react-router-dom";

function wrapper(group_name, content) {
  return (
    <SoftBox pt={{xxs: 1, xs: 3}} pb={{xxs: 0, xs: 3}} px={{xxs: 1, xs: 3}} height={{xxs: 'calc(100% - 16px)', xs: 'calc(100% - 50px)'}}
      sx={({ palette: { white }, boxShadows: { navbarBoxShadow }, borders: { borderRadius }, functions: { rgba, pxToRem } }) => ({
        boxShadow: navbarBoxShadow,
        backdropFilter: `saturate(200%) blur(${pxToRem(4)})`,
        // backgroundColor: rgba(white.main, 0.8),
        borderRadius: borderRadius.xl,
        overflowY: 'auto',
        '::-webkit-scrollbar': {
          backgroundColor: 'transparent',
          width: '8px',
        },
        '::-webkit-scrollbar-track': { /* background of the scrollbar except button or resizer */
          backgroundColor: 'transparent',
        },
        '::-webkit-scrollbar-thumb': { /* scrollbar itself */
          backgroundColor: '#babac0',
          borderRadius: '6px',
          border: `1px solid ${rgba(white.main, 0.8)}`,
        },
        '::-webkit-scrollbar-button': { /* set button(top and bottom of the scrollbar) */
          display: 'none',
        },
      })}
    >
      <Grid container>
        <Grid item xxs={12}>
          <SoftBox mb={1}>
            <SoftTypography pl={1} variant="h4">
              Subgroups
            </SoftTypography>
          </SoftBox>
          <SoftBox mb={2}>
            <SoftTypography pl={1} variant="body2" color="text">
              {group_name}
            </SoftTypography>
          </SoftBox>
        </Grid>
      </Grid>
      <SoftBox mt={{ xxs: 1, lg: 3 }} mb={1}>
        {content}
      </SoftBox>
    </SoftBox>
  )
}

function DeviceGroups({ map, navTitle, setNavTitle, sidebarState, setSidebarState }) {
  // DevicePreviewCard dropdown menu state

  groupAPI.loadToken(useAuth0());
  bksAPI.loadToken(useAuth0());
  userAPI.loadToken(useAuth0());

  const { user } = useAuth0();

  const { group_id } = useMatch('groups/:group_id/subgroups').params;

  const group = useQuery({
    queryKey: ['getGroupWithID', group_id],
    queryFn: async () => {
      return await groupAPI.getGroupWithID(group_id);
    }
  });

  const devices = useQuery({
    queryKey: ['getAllDevices'],
    queryFn: async () => {
      return await bksAPI.getAllDevices();
    }
  });

  const selected_user = useQuery({
    queryKey: ['getUserByEmail', user.email.toLowerCase()],
    queryFn: async () => {
      return await userAPI.getUserByEmail(user.email.toLowerCase());
    }
  });

  useEffect(() => {
    setSidebarState('sidenav');
    setNavTitle(['groups', 'subgroups']);
  }, [sidebarState, navTitle]);

  if (group.isLoading || devices.isLoading || selected_user.isLoading) {
    return wrapper('Loading...');
  }

  if (group.error || devices.error || selected_user.error) {
    if(group.error){
      var error = group.error;
    }
    else if (devices.error){
      var error = devices.error;
    }
    else if (selected_user.error){
      var error = selected_user.error;
    }
    return wrapper("An error has occurred: " + error.message);
  }

  if (!group.data || !devices.data || !selected_user.data) {
    return wrapper('Loading...');
  }

  var group_data = processGroupData(group.data, devices.data);

  // console.log(group_data)

  return wrapper((group_data.name),
    (
      <Grid container spacing={3}>
        {
          group_data.subgroups.map(function (subgroup) {
            // console.log(subgroup);
            return (
              <Grid item xxs={12} md={6} x3l={4} key={subgroup.group_id} onClick={() => { subgroup.group_id }} >
                <SubgroupCard group={group_data} subgroup={subgroup} user_role={selected_user.data.user.user_type} />
              </Grid>)
          })
        }
      </Grid>
    )
  );
}

DeviceGroups.propTypes = {
  map: PropTypes.object,
  navTitle: PropTypes.array,
  setNavTitle: PropTypes.func,
  sidebarState: PropTypes.string,
  setSidebarState: PropTypes.func,
}

export default DeviceGroups;
